import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ValueChangeProperties } from '@rivethealth/data/analytics/event';
import { Size } from '../core/size.service';

export type InputSize = 'small' | 'medium' | 'large';
/**
 * @deprecated Use riv-text-field, riv-alphabetic-field, riv-number-field, riv-currency-field, or riv-percentage-field instead.
 * @see https://rivethealth.github.io/design-system/
 */
@Component({
  selector: 'deprecated-text-field',
  templateUrl: './text-field.component.html',
  styleUrls: ['./text-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DeprecatedTextFieldComponent),
    },
  ],
})
export class DeprecatedTextFieldComponent implements ControlValueAccessor {
  _readonly: boolean;

  prefixElement: Size | undefined;
  suffixElement: Size | undefined;
  suffixBufferElement: Size | undefined;
  prefixBufferElement: Size | undefined;
  inputLengthElement: Size | undefined;

  @Input() case: 'upper' | 'lower' | undefined;
  @Input() label: string;
  @Input() combinedRight: boolean = false;
  @Input() combinedLeft: boolean = false;
  @Input() help: string;
  @Input() required: boolean;
  @Input() disabled: boolean;
  @Input()
  set readonly(value) {
    this._readonly = value !== false;
  }
  get readonly(): boolean {
    return this._readonly;
  }
  @Input() prefix: TemplateRef<any>;
  @Input() suffix: TemplateRef<any>;
  @Input() maxLength: number | null = null;
  @Input() minLength: number | null = null;
  @Input() placeholder: string = '';
  @Input() autoFocus: boolean;
  @Input() min: number;
  @Input() max: number;
  @Input() error = false;
  @Input() warning = false;
  @Input() size: InputSize = 'medium';
  @Input()
  type: 'text' | 'email' | 'number' | 'password' | 'search' | 'tel' | 'url' =
    'text';
  @Input() labelDirection: 'row' | 'column' | undefined;
  @Input() textAlign: 'left' | 'right' | 'center' = 'left';
  @Input() rightAlignedPrefix: boolean = false;
  @Input() rightAlignedSuffix: boolean = false;
  @Input() fontSize: string;
  @Input() actionText: string;
  @Input() step: number;

  @Output() blur = new EventEmitter();
  @Output() focusEvent = new EventEmitter();
  @Output() action = new EventEmitter();
  @Output() keydown = new EventEmitter<KeyboardEvent>();

  @ViewChild('inputElement', { static: false }) inputElement: ElementRef;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  private casedValue(value: string): string {
    switch (this.case) {
      case 'lower':
        return value && value.toLowerCase();
      case 'upper':
        return value && value.toUpperCase();
      default:
        return value;
    }
  }

  private _value: string | null;
  get value() {
    return this._value;
  }
  set value(value) {
    if (this.type === 'number' && value === '') {
      value = null;
    }
    if (value != this._value && this.analyticsEvents.change) {
      this.analyticsEvents.change({
        previousValue: this._value,
        newValue: value,
      });
    }
    this._value = value;
    this.propagateChange(this.casedValue(value!));
  }

  propagateChange = (_: any) => {};

  writeValue(value: any) {
    this._value = value;
    if (this.inputElement != null) {
      this.inputElement.nativeElement.value = value;
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (_: any) => {}) {
    this.propagateChange = fn;
  }

  registerOnTouched() {}

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
    this.cdr.markForCheck();
  }

  focus() {
    this.inputElement.nativeElement.focus();
  }

  onInputBlur() {
    if (this.type === 'number') {
      if (typeof this.min === 'number' && +this.value! < this.min) {
        this.value = this.min.toString();
      }
      if (typeof this.max === 'number' && +this.value! > this.max) {
        this.value = this.max.toString();
      }
    }
  }

  @Input()
  analyticsEvents: {
    change?: (properties: ValueChangeProperties) => void;
  } = {};

  get classes() {
    return {
      'rv-input': true,
      disabled: this.disabled,
      value: this.value ? this.case : undefined,
      [this.size || 'medium']: true,
      'disabled-arrows': this.rightAlignedSuffix,
    };
  }

  get suffixClasses() {
    return {
      'suffix-element': true,
      'right-align-suffix': this.rightAlignedSuffix,
      'left-align-ix': !this.rightAlignedSuffix,
    };
  }

  get prefixClasses() {
    return {
      'prefix-element': true,
      'right-align-prefix': this.rightAlignedPrefix,
      'left-align-ix': !this.rightAlignedPrefix,
    };
  }
}
